import { computed, ComputedRef, inject, Ref, watch } from "vue";
import type { Point } from "@/stores/line";
import { useEditorStore } from "@/stores/editor_store";

import Konva from "konva";
import { PersistentStore } from "@/stores/persistent_store";
import { EditorStage } from "@/modules/editor/stage";

interface UseZoomableArgs {
  stage: Ref<Konva.Stage>;
  stageOriginalBg: Ref<Konva.Stage>;
  initialScale: Ref<number>;
  isProcessing: Ref<boolean> | ComputedRef<boolean>;
}

const DEFAULT_ZOOM_LEVEL = 100;
export const MAX_ZOOM_LEVEL = 600;

/**
 * useZoomable is a composable that provides the zooming functionality to the canvas
 */
export function useZoomable(args: UseZoomableArgs): UseZoomableReturnType {
  let { stage, stageOriginalBg, initialScale, isProcessing } = args;
  const store = useEditorStore();
  const currentPersistentStore = inject<Ref<PersistentStore>>("currentPersistentStore");

  let editorStage = new EditorStage(stage.value, stageOriginalBg.value);
  watch([stage, stageOriginalBg], () => {
    editorStage = new EditorStage(stage.value, stageOriginalBg.value);
  });

  const canZoomIn = computed(() => {
    return currentPersistentStore.value?.zoomLevel < MAX_ZOOM_LEVEL && !isProcessing.value;
  });

  const canZoomOut = computed(() => {
    return currentPersistentStore.value?.zoomLevel > DEFAULT_ZOOM_LEVEL && !isProcessing.value;
  });

  const zoomCursorType = computed(() => {
    const currentZoomLevel = currentPersistentStore.value?.zoomLevel;
    return currentZoomLevel === MAX_ZOOM_LEVEL ? "zoom-out" : "zoom-in";
  });

  const calculateScale = (): number => {
    const baseScale = initialScale.value;
    let zoomLevel = currentPersistentStore.value?.zoomLevel;

    if (currentPersistentStore.value?.defaultZoomLevel < 0) {
      // This means it's the initial zoom ie is triggered for the first time
      // In this case we need to set the zoom level that matches the base level.
      // From the next time onwards it'll hold the zoom value changed by user.
      zoomLevel = baseScale * 100;
      currentPersistentStore.value?.setDefaultZoom(100);
    }

    const increment = zoomLevel - 100; // (110 - 100 = 10);     (130 - 100 = 30)
    const newScale = baseScale + increment / 100; // eg: 0.5 + 10/100 = 0.6  or 0.9 + (-10/100) = 0.8
    return Math.round((newScale + Number.EPSILON) * 1000) / 1000;
  };

  const calculateCenterPosition = (scale: number): Point => {
    const theStage = stage.value.getStage();
    const stageWidth = theStage.width();
    const stageHeight = theStage.height();
    const imageWidth = store.selectedImage.meta.previewWidth;
    const imageHeight = store.selectedImage.meta.previewHeight;

    return {
      x: Math.floor((-imageWidth * scale) / 2 + stageWidth / 2),
      y: Math.floor((-imageHeight * scale) / 2 + stageHeight / 2),
    };
  };

  const calculateFocusPointPosition = (focusPoint: Point, pointerPosition: Point, scale: number): Point => {
    return {
      x: -(focusPoint.x - pointerPosition.x / scale) * scale,
      y: -(focusPoint.y - pointerPosition.y / scale) * scale,
    };
  };

  const updateStagesWithAnimation = (updates: { scale?: number; position?: Point }, opts?: ZoomOpts) => {
    if (opts?.animate) {
      editorStage.updateStages({
        ...updates,
        animate: true,
        duration: 0.25,
        easing: "backEaseOut",
      });
    } else {
      editorStage.updateStages(updates);
    }
  };

  /**
   * Ensures the image stays within the visible bounds of the canvas after zooming or panning.
   * This prevents users from accidentally moving or zooming the image so far that it becomes inaccessible,
   * maintaining a usable and intuitive editing experience.
   */
  const clampPos = (pos: Point, stage: Konva.Stage, options?: { mode?: "strict" | "flexible" }): Point => {
    const theStage = stage.getStage();
    const canvasWidth = theStage.width();
    const canvasHeight = theStage.height();
    const scale = theStage.scaleX();
    const contentWidth = store.selectedImage.meta.previewWidth;
    const contentHeight = store.selectedImage.meta.previewHeight;

    const scaledWidth = contentWidth * scale;
    const scaledHeight = contentHeight * scale;

    let minX: number, maxX: number, minY: number, maxY: number;

    if (options?.mode === "flexible") {
      // For zoom operations, we allow more flexibility in positioning
      // Only clamp if the image would be completely outside the canvas
      minX = -scaledWidth + canvasWidth * 0.1; // Allow 10% of canvas width
      maxX = canvasWidth * 0.1; // Allow 10% of canvas width
      minY = -scaledHeight + canvasHeight * 0.1; // Allow 10% of canvas height
      maxY = canvasHeight * 0.1; // Allow 10% of canvas height
    } else {
      // Default/Strict mode - very restrictive clamping (backward compatible)
      minX = Math.min(0, canvasWidth - scaledWidth);
      maxX = scaledWidth > canvasWidth ? 0 : canvasWidth - scaledWidth;
      minY = Math.min(0, canvasHeight - scaledHeight);
      maxY = scaledHeight > canvasHeight ? 0 : canvasHeight - scaledHeight;
    }

    pos.x = Math.max(Math.min(pos.x, maxX), minX);
    pos.y = Math.max(Math.min(pos.y, maxY), minY);

    return pos;
  };

  const repositionAfterCenterZoom = () => {
    const scale = calculateScale();
    const centerPos = calculateCenterPosition(scale);

    // Reset to center for default zoom level
    if (currentPersistentStore.value?.zoomLevel === DEFAULT_ZOOM_LEVEL) {
      centerPos.x = 0;
      centerPos.y = 0;
    }

    editorStage.updateStages({
      position: centerPos,
      animate: false,
    });
  };

  const repositionAfterFocusPointZoom = (focusPoint: Point, pointerPosition: Point, opts?: ZoomOpts) => {
    if (!focusPoint || !pointerPosition) return;

    const theStage = stage.value.getStage();
    const scale = theStage.scaleX();
    const pos = calculateFocusPointPosition(focusPoint, pointerPosition, scale);

    // Reset to center for default zoom level
    if (currentPersistentStore.value?.zoomLevel === DEFAULT_ZOOM_LEVEL) {
      pos.x = 0;
      pos.y = 0;
    }

    const newPos = clampPos(pos, stage.value);
    updateStagesWithAnimation({ position: newPos }, opts);
  };

  const applyZoomWithFocusPoint = (focusPoint: Point, pointerPosition: Point, opts?: ZoomOpts) => {
    if (!focusPoint || !pointerPosition) {
      applyZoom();
      return;
    }

    const newScale = calculateScale();
    const pos = calculateFocusPointPosition(focusPoint, pointerPosition, newScale);
    const newPos = clampPos(pos, stage.value, { mode: "flexible" });
    updateStagesWithAnimation({ scale: newScale, position: newPos }, opts);
  };

  const applyZoom = (callback?: () => void) => {
    const newScale = calculateScale();
    editorStage.updateStages({ scale: newScale });
    callback?.();
  };

  /**
   * zoom is a function that zooms the editor.
   * If the selectedImage has the last_pointer_position and last_focus_point, this function will zoom based on this focal point
   * If they are missing, it'll just zoom from the center
   */
  const zoom = (opts?: ZoomOpts) => {
    // Zoom from the center if we don't have last pointer position and focus point
    if (!store.selectedImage?.meta?.last_pointer_position || !store.selectedImage?.meta?.last_focus_point) {
      repositionAfterCenterZoom();
      return;
    }

    const focusPoint = store.selectedImage?.meta?.last_focus_point;
    const pointerPosition = store.selectedImage?.meta?.last_pointer_position;

    repositionAfterFocusPointZoom(focusPoint, pointerPosition, opts);
  };

  /**
   * zoomOut zooms out the given stage. If a zoom factor is provided, it zooms out with the provided factor.
   *   If not, the default zoom factor is 10.
   *   The callback is invoked after the zoom is applied.
   * @param args
   * @example
   *    zoomOut({ zoomFactor: 15, callback: () => {} })
   */
  const zoomOut = (args?: ZoomArgs) => {
    const { zoomFactor = 10, callback = () => {} } = args || {};
    if (canZoomOut.value) {
      currentPersistentStore.value?.zoomOut(zoomFactor);
      applyZoom(callback);
      zoom();
    }
  };

  /**
   * zoomIn zooms in the given stage. If a zoom factor is provided, it zooms out with the provided factor.
   *   If not, the default zoom factor is 10.
   *   The callback is invoked after the zoom is applied.
   * @param args
   * @example
   *    zoomIn({ zoomFactor: 15, callback: () => {} })
   */
  const zoomIn = (args?: ZoomArgs) => {
    const { zoomFactor = 10, callback = () => {} } = args || {};
    if (canZoomIn.value) {
      currentPersistentStore.value?.zoomIn(zoomFactor);
      applyZoom(callback);
      zoom();
    }
  };

  /**
   * resetZoom resets the zoom to the default level and repositions the image from the center
   */
  const resetZoom = () => {
    setZoom(DEFAULT_ZOOM_LEVEL);
    applyZoom();
    repositionAfterCenterZoom();
  };

  const setZoom = (value: number): void => currentPersistentStore.value?.setZoom(value);

  const setDefaultZoomLevel = () => setZoom(DEFAULT_ZOOM_LEVEL);

  return {
    zoom,
    zoomIn,
    zoomOut,
    applyZoom,
    applyZoomWithFocusPoint,
    resetZoom,
    canZoomOut,
    canZoomIn,
    repositionAfterFocusPointZoom,
    repositionAfterCenterZoom,
    setDefaultZoomLevel,
    setZoom,
    clampPos,
    zoomCursorType,
  };
}

interface ZoomArgs {
  zoomFactor?: number;
  callback?: () => void;
}

interface ZoomOpts {
  animate?: boolean;
}

export interface UseZoomableReturnType {
  zoom: (opts?: ZoomOpts) => void;
  zoomIn: (args?: ZoomArgs) => void;
  zoomOut: (args?: ZoomArgs) => void;
  applyZoom: (callback?: () => void) => void;
  applyZoomWithFocusPoint: (focusPoint: Point, pointerPosition: Point, opts?: ZoomOpts) => void;
  resetZoom: () => void;
  canZoomOut: ComputedRef<boolean>;
  canZoomIn: ComputedRef<boolean>;
  repositionAfterFocusPointZoom: (focusPoint: Point, pointerPosition: Point, opts?: ZoomOpts) => void;
  repositionAfterCenterZoom: () => void;
  setDefaultZoomLevel: () => void;
  setZoom: (value: number) => void;
  clampPos: (pos: Point, stage: Konva.Stage, options?: { mode?: "strict" | "flexible" }) => Point;
  zoomCursorType: ComputedRef<"zoom-in" | "zoom-out">;
}
